/**
 * A function that returns a form field component based on the given field object.
 *
 * @param {Object} field - The field object containing the properties of the form field.
 */
import { createElement } from "commonAssets/lib/createElement.js";
import { i18n } from "commonAssets/js/constants";
import "./IntlPhoneInput.js";
import "./SsnInput.js";

/**
 * Generates a form field element based on the provided field type.
 *
 * @param {object} field - The field object containing information about the field.
 * @param {string} className - The CSS class name for the field element.
 * @param {string} fieldID - The ID attribute for the field element.
 * @param {object} element - The generated field element.
 * @param {object} validation - The validation rules for the field.
 * @param {object} container - The container element for the field.
 * @returns {object} - The generated field element.
 */
export const FormFields = (
    field,
    className,
    fieldID,
    element,
    validation,
    container
) => {
    element = null; // reset element
    switch (field.type) {
        case "header": // <h1><h2><h3><h4><h5><h6>
            element = heading(field, className, fieldID, validation);
            break;
        case "paragraph": // <p>
            element = paragraph(field, className, fieldID, validation);
            break;
        case "text": // <input type="text">
            element = text(field, className, fieldID, validation);
            break;
        case "number": // <input type="number"> // ada compliance updates - added
            element = number(field, className, fieldID, validation);
            break;
        case "tel": // <input type="tel"> // ada compliance updates - added
            element = tel(field, className, fieldID, validation);
            break;
        case "date": // <input type="date">
            element = date(field, className, fieldID, validation);
            break;
        case "textarea": // <textarea>
            element = textarea(field, className, fieldID, validation);
            break;
        case "select": // <select>
            element = select(field, className, fieldID, validation);
            selectOptionForEach(field, element);
            break;
        case "radio-group":
            element = radioGroup(field, className, fieldID, validation);
            radioGroupForEach(field, element);
            break;
        case "checkbox-group":
            element = checkboxGroup(field, className, fieldID, validation);
            checkboxGroupForEach(field, element);
            break;
        case "hidden":
            element = hiddenField(field, className, fieldID, validation);
            break;
        case "spacer": // <[div] class="[spacer]"></[div]> // ada compliance updates - added
            element = spacer(field, className, fieldID, validation);
            break;
        case "autocomplete": // ada compliance updates - added
            element = autocomplete(field, className, fieldID, validation);
            autocompleteDatalist(field, fieldID, element);
            break;
        case "intl-phone-input":
            field.validation = "phone";
            element = tel(field, className, fieldID, validation);
            break;
        case "ssn-input":
            element = ssnInput(field);
            break;
        default:
            console.error(`Unknown field type "${field.type}"`);
            return;
    }
    return element;
};

export const FormFieldsLabel = (field, fieldID, div) => {
    const fieldLabel = i18n.getField(field.label);
    const star = field.required ? "*" : "";
    if (
        field.type === "text" ||
        field.type === "autocomplete" ||
        field.type === "number" ||
        field.type === "date" ||
        field.type === "textarea" ||
        field.type === "select" ||
        field.type === "ssn-input" ||
        field.type === "intl-phone-input"
    ) {
        const label = createElement(
            "label",
            { for: field.name },
            [],
            `${fieldLabel}${star} <span class="error"></span>`
        );
        div.appendChild(label);
    }
};

const requiredField = (field) => {
    if (field.required) {
        return { required: "" };
    }
    return {};
};

const fieldAttribute = (field, attr) => {
    if (field[attr]) {
        return { [attr]: field[attr] };
    }
    return {};
};

const heading = (field, className, fieldID, validation) => {
    return createElement(field.subtype, { class: className }, [], field.label);
};

const paragraph = (field, className, fieldID, validation) => {
    return createElement(field.subtype, { class: className }, [], field.label);
};

const text = (field, className, fieldID, validation) => {
    validation = "";
    if (field.required) {
        validation = "required";
    }
    if (field.validation) {
        validation += ` ${field.validation}`;
    }
    return createElement("input", {
        type: "text",
        name: field.name,
        class: field.className,
        id: field.name,
        "data-validate": validation,
        "data-label": field.dataLabel || field.label,
        ...requiredField(field),
    });
};

const number = (field, className, fieldID, validation) => {
    validation = "";
    if (field.required) {
        validation = "required";
    }
    if (field.validation) {
        validation += ` ${field.validation}`;
    }
    return createElement("input", {
        type: "number",
        name: field.name,
        class: field.className,
        id: field.name,
        "data-validate": validation,
        "data-label": field.dataLabel || field.label,
        min: field.min !== undefined ? field.min : 0,
        max: field.max !== undefined ? field.max : ``,
        step: field.step !== undefined ? field.step : ``,
        ...fieldAttribute(field, "placeholder"),
        ...requiredField(field),
    });
};

const tel = (field, className, fieldID, validation) => {
    validation = "";
    if (field.required) {
        validation = "required";
    }
    if (field.validation) {
        validation += ` ${field.validation}`;
    }
    return createElement("input", {
        type: "tel",
        name: field.name,
        class: field.className,
        id: field.name,
        "data-validate": validation,
        "data-label": field.dataLabel || field.label,
        // pattern: field.pattern !== undefined ? field.pattern : ``,
        ...requiredField(field),
    });
};

const date = (field, className, fieldID, validation) => {
    validation = "date";
    if (field.required) {
        validation += " required";
    }
    if (field["data-date-min"]) {
        validation += " date-min";
    }
    if (field["data-date-max"]) {
        validation += " date-max";
    }
    if (field["data-date-max"] === "today") {
        field["data-date-max"] = new Date().toISOString().split('T')[0];
    }
    return createElement("input", {
        type: "date",
        name: field.name,
        class: field.className,
        id: field.name,
        "data-validate": validation,
        "data-label": field.dataLabel || field.label,
        "data-date-format": field["data-date-format"] || "YYYY-MM-DD",
        "data-date-min": field["data-date-min"] || "",
        "data-date-max": field["data-date-max"] || "",
        min: field["data-date-min"] || "",
        max: field["data-date-max"] || "",
        ...requiredField(field),
    });
};

const textarea = (field, className, fieldID, validation) => {
    return createElement("textarea", {
        name: field.name,
        class: field.className,
        id: field.name,
        "data-validate": field.required ? "required " : "",
        "data-label": field.dataLabel || field.label,
        ...fieldAttribute(field, "placeholder"),
        ...fieldAttribute(field, "rows"),
        ...fieldAttribute(field, "cols"),
        ...requiredField(field),
    });
};

const select = (field, className, fieldID, validation) => {
    return createElement("select", {
        name: field.name,
        class: field.className,
        id: field.name,
        "data-validate": field.required ? "required " : "",
        "data-label": field.dataLabel || field.label,
        ...requiredField(field),
    });
};

const selectOptionForEach = (field, element) => {
    field.values.forEach((option) => {
        const optionElement = createElement(
            "option",
            {
                value: option.value ? option.value : "",
            },
            [],
            option.label
        );
        element.appendChild(optionElement);
    });
};

const radioGroup = (field, className, fieldID, validation) => {
    return createElement(
        "div",
        { class: "" },
        [createElement("p", {}, [], field.description)],
        ``
    );
};

const radioGroupForEach = (field, element) => {
    let optionID = 0;
    field.values.forEach((option, index) => {
        optionID++; // start with 1
        const inputElement = createElement("input", {
            type: "radio",
            name: field.name,
            class: "form-check-input",
            id: field.name + "_" + optionID,
            value: option.value,
            "data-validate": field.required ? "required " : "",
            "data-label": field.dataLabel || field.label,
            ...requiredField(field),
        });

        const labelElement = createElement(
            "label",
            {
                for: field.name + "_" + optionID,
                class: "form-check-label",
            },
            [],
            option.label + ' <span class="error"></span>'
        );

        const divElement = createElement("div", {
            class: "form-check",
        });

        divElement.appendChild(inputElement);
        divElement.appendChild(labelElement);

        element.appendChild(divElement);
    });
};

const checkboxGroup = (field, className, fieldID, validation) => {
    return createElement("div", {
        class: "form-check" + className ? className : "",
    });
};
// for checkboxGroup() function
const checkboxGroupForEach = (field, element) => {
    let checkboxID = 0;
    field.values.forEach((option, index) => {
        const input = createElement("input", {
            type: "checkbox",
            name: field.name,
            class: "form-check-input",
            id: field.name + "_" + checkboxID,
            value: option.value,
            "data-validate": field.required ? "required " : "",
            "data-label": field.dataLabel || field.label,
            ...requiredField(field),
        });
        const label = createElement(
            "label",
            {
                for: field.name + "_" + checkboxID,
                class: "form-check-label",
            },
            [],
            option.label + '<span class="error"></span>'
        );

        const divElement = createElement(
            "div",
            {
                class: field.inline ? "form-check-inline" : "form-check",
            },
            [input, label]
        );
        element.appendChild(divElement);
        checkboxID++;
    });
};

const hiddenField = (field, className, fieldID, validation) => {
    return createElement("input", {
        type: "hidden",
        name: field.name,
        id: field.name,
        "data-label": field.dataLabel || field.label,
        ...requiredField(field),
    });
};

const spacer = (field, className, fieldID, validation) => {
    const fieldSubType = field.subtype ? field.subtype : "div";
    const fieldClass = field.className ? field.className : "spacer";
    return createElement(fieldSubType, {
        class: fieldClass,
    });
};

const autocomplete = (field, className, fieldID, validation) => {
    // fieldInput
    const fieldInput = createElement("input", {
        type: "text",
        name: field.name,
        class: field.className,
        id: field.name,
        list: field.name + "_" + fieldID + "_list",
        "data-validate": field.required ? "required " : "",
        "data-label": field.dataLabel || field.label,
        ...requiredField(field),
    });
    // if field.required, add required attribute at end of <input>
    if (field.required) {
        fieldInput.setAttribute("required", "");
    }

    // element wrapper
    return createElement(
        "div",
        {
            class: "autoComplete_wrapper",
            id: "autoComplete_wrapper_" + fieldID,
        },
        [fieldInput]
    );
};

const autocompleteDatalist = (field, fieldID, element) => {
    const datalist = createElement("datalist", {
        id: field.name + "_" + fieldID + "_list",
    });
    field.values.forEach((option) => {
        const optionElement = createElement(
            "option",
            {
                value: option.value,
            },
            [],
            option.label
        );
        datalist.appendChild(optionElement);
    });
    element.appendChild(datalist);
};

const intlPhoneInput = (field) => {
    return createElement("intl-phone-input", {
        name: field.name,
        class: field.className,
        id: field.name,
        "data-label": field.label,
        "data-validate": field.validation,
        ...requiredField(field),
    });
};

const ssnInput = (field) => {
    return createElement("ssn-input", {
        name: field.name,
        class: field.className,
        id: field.name,
        "data-label": field.label,
        ...requiredField(field),
    });
};
